import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import RequestCard from '../components/lifelink/RequestCard';
import MapView from '../components/lifelink/MapView';
import FileGallery from '../components/lifelink/FileGallery';
import ChatPanel from '../components/lifelink/ChatPanel';
import PeriodicCheckin from '../components/lifelink/PeriodicCheckin';
import { BarChart3, AlertTriangle, Clock, CheckCircle2, Activity, Filter, RefreshCw, MessageCircle } from 'lucide-react';
import axios from 'axios';
import useSEO from '../hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  useSEO({ title: 'Dashboard Operativa', description: 'Dashboard operativa LIFE LINKE con statistiche, mappa e richieste in tempo reale.', noindex: true });
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [filters, setFilters] = useState({ categoria: '', priorita: '', stato: '' });
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('lifelink_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [statsRes, reqRes] = await Promise.all([
        axios.get(`${API}/requests/stats`, { headers }),
        axios.get(`${API}/requests`, {
          params: { ...filters, limit: 20 },
          headers
        })
      ]);
      setStats(statsRes.data);
      setRequests(reqRes.data.requests || []);
    } catch (e) {
      console.error('Dashboard fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const updateStatus = async (requestId, newStatus) => {
    try {
      const token = localStorage.getItem('lifelink_token');
      await axios.put(`${API}/requests/${requestId}/status`, { stato: newStatus }, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      fetchData();
      setSelectedRequest(null);
    } catch (e) {
      console.error('Status update error:', e);
    }
  };

  const statCards = stats ? [
    { label: t('richieste_oggi'), value: stats.oggi, icon: AlertTriangle, color: '#FF3B30' },
    { label: t('interventi_in_corso'), value: stats.in_corso, icon: Clock, color: '#FF9F0A' },
    { label: t('richieste_totali'), value: stats.totale, icon: Activity, color: '#0A84FF' },
    { label: t('richieste_risolte'), value: stats.risolte, icon: CheckCircle2, color: '#32D74B' },
  ] : [];

  const mapRequests = requests.filter(r => r.latitudine && r.longitudine);

  return (
    <div className="min-h-screen bg-[#E8F4FD] pt-20 pb-8" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <h1 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl font-black uppercase tracking-tight text-[#0D1B2A]">
              {t('dashboard_operativa')}
            </h1>
            <span className="flex items-center gap-1 px-2 py-1 bg-[#32D74B]/20 border border-[#32D74B]/50">
              <span className="w-2 h-2 bg-[#32D74B] rounded-full animate-pulse" />
              <span className="text-[10px] text-[#32D74B] font-mono uppercase tracking-widest">{t('in_tempo_reale')}</span>
            </span>
          </div>
          <button onClick={fetchData} className="p-2 border border-[#B8D4E8] hover:border-[#FF3B30] transition-all" data-testid="refresh-dashboard-btn">
            <RefreshCw size={16} className="text-[#4A6B8A]" />
          </button>
        </div>

        {/* Stats Cards */}
        {user && user.role === 'civile' && (
          <div className="mb-6"><PeriodicCheckin /></div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {statCards.map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="border border-[#B8D4E8] bg-white p-4" data-testid={`stat-card-${label}`}>
              <div className="flex items-center gap-2 mb-2">
                <Icon size={14} style={{ color }} />
                <span className="text-[10px] text-[#6B8AB5] uppercase tracking-widest font-bold">{label}</span>
              </div>
              <span className="text-3xl font-['JetBrains_Mono'] font-bold text-[#0D1B2A]">{value ?? '-'}</span>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-6 flex-wrap" data-testid="dashboard-filters">
          <Filter size={14} className="text-[#4A6B8A]" />
          <select
            value={filters.stato}
            onChange={(e) => setFilters(f => ({ ...f, stato: e.target.value }))}
            className="bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-2 text-xs uppercase tracking-widest focus:border-[#FF3B30] focus:outline-none"
            data-testid="filter-stato"
          >
            <option value="">{`Stato: ${t('tutti')}`}</option>
            {['nuova', 'in_corso', 'assegnata', 'risolta', 'chiusa'].map(s => (
              <option key={s} value={s}>{t(s)}</option>
            ))}
          </select>
          <select
            value={filters.priorita}
            onChange={(e) => setFilters(f => ({ ...f, priorita: e.target.value }))}
            className="bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-2 text-xs uppercase tracking-widest focus:border-[#FF3B30] focus:outline-none"
            data-testid="filter-priorita"
          >
            <option value="">{`${t('priorita')}: ${t('tutti')}`}</option>
            {['critica', 'alta', 'media', 'bassa'].map(p => (
              <option key={p} value={p}>{t(p)}</option>
            ))}
          </select>
          <select
            value={filters.categoria}
            onChange={(e) => setFilters(f => ({ ...f, categoria: e.target.value }))}
            className="bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-2 text-xs uppercase tracking-widest focus:border-[#FF3B30] focus:outline-none"
            data-testid="filter-categoria"
          >
            <option value="">{`Categoria: ${t('tutti')}`}</option>
            {['richiesta_aiuto','intervento_urgente','sfollati','rifugio','farmaci','aiuto_umanitario','cibo','emergenza_medica','acqua_igiene','protezione_minori','evacuazione_trasporto','persone_scomparse','danni_infrastrutturali','blackout_comunicazioni','checkin_sto_bene'].map(c => (
              <option key={c} value={c}>{t(c)}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Requests List */}
          <div className="lg:col-span-2 space-y-3">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#4A6B8A]">{t('richieste_recenti')}</h2>
            </div>
            {loading ? (
              <div className="text-center py-12 text-[#6B8AB5] text-sm">{t('caricamento')}</div>
            ) : requests.length === 0 ? (
              <div className="text-center py-12 text-[#6B8AB5] text-sm border border-[#B8D4E8] bg-white">{t('nessun_risultato')}</div>
            ) : (
              requests.map(req => (
                <RequestCard key={req.id} request={req} onClick={() => setSelectedRequest(req)} />
              ))
            )}
          </div>

          {/* Map + Detail */}
          <div className="space-y-4">
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#4A6B8A]">{t('mappa_live')}</h2>
            <MapView requests={mapRequests} height="300px" />

            {/* Priority Stats */}
            {stats && (
              <div className="border border-[#B8D4E8] bg-white p-4">
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#4A6B8A] mb-3">
                  <BarChart3 size={12} className="inline mr-1" />
                  {t('statistiche')}
                </h3>
                <div className="space-y-2">
                  {Object.entries(stats.per_priorita || {}).map(([key, val]) => {
                    const colors = { critica: '#FF3B30', alta: '#FF9F0A', media: '#FFD60A', bassa: '#32D74B' };
                    const total = stats.totale || 1;
                    return (
                      <div key={key} className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-widest w-16 text-[#4A6B8A]">{t(key)}</span>
                        <div className="flex-1 h-2 bg-[#E8F4FD] border border-[#B8D4E8]">
                          <div
                            className="h-full transition-all"
                            style={{ width: `${(val / total) * 100}%`, backgroundColor: colors[key] }}
                          />
                        </div>
                        <span className="text-xs font-mono text-[#0D1B2A] w-8 text-right">{val}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Request Detail Modal */}
        {selectedRequest && (
          <RequestDetailModal
            request={selectedRequest}
            onClose={() => setSelectedRequest(null)}
            onUpdateStatus={updateStatus}
            user={user}
            t={t}
          />
        )}
      </div>
    </div>
  );
}

function RequestDetailModal({ request, onClose, onUpdateStatus, user, t }) {
  const [newStatus, setNewStatus] = useState(request.stato);
  const isOperator = user && (user.role === 'admin' || user.role === 'operatore');

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="request-detail-modal">
      <div className="bg-white border border-[#B8D4E8] w-full max-w-lg max-h-[80vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-[#0D1B2A] mb-4">
          {t(request.categoria)}
        </h2>
        <div className="space-y-3 text-sm">
          <p className="text-[#4A6B8A]">{request.descrizione}</p>
          {request.indirizzo && <p className="text-[#6B8AB5] text-xs">{request.indirizzo}</p>}
          {request.nome_richiedente && <p className="text-[#6B8AB5] text-xs">Richiedente: {request.nome_richiedente}</p>}
          {request.telefono && <p className="text-[#6B8AB5] text-xs">Tel: {request.telefono}</p>}
          {request.numero_persone && <p className="text-[#6B8AB5] text-xs">Persone: {request.numero_persone}</p>}

          {/* File attachments */}
          <FileGallery requestId={request.id} />

          {/* Status history */}
          {request.storico_stati && (
            <div className="border-t border-[#B8D4E8] pt-3 mt-3">
              <span className="text-[10px] uppercase tracking-widest text-[#6B8AB5]">Storico stati</span>
              <div className="mt-2 space-y-1">
                {request.storico_stati.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="w-2 h-2 bg-[#FF3B30] rounded-full" />
                    <span className="text-[#0D1B2A] uppercase">{t(s.stato)}</span>
                    <span className="text-[#6B8AB5]">{new Date(s.timestamp).toLocaleString('it-IT')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Status update for operators */}
          {isOperator && (
            <div className="border-t border-[#B8D4E8] pt-3 mt-3">
              <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">{t('aggiorna_stato')}</label>
              <div className="flex gap-2">
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="flex-1 bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-2 text-xs focus:border-[#FF3B30] focus:outline-none"
                  data-testid="status-select"
                >
                  {['nuova', 'in_corso', 'assegnata', 'risolta', 'chiusa'].map(s => (
                    <option key={s} value={s}>{t(s)}</option>
                  ))}
                </select>
                <button
                  onClick={() => onUpdateStatus(request.id, newStatus)}
                  className="px-4 py-2 bg-[#FF3B30] text-[#0D1B2A] text-xs font-bold uppercase tracking-widest hover:bg-[#FF1A1A] transition-all"
                  data-testid="update-status-btn"
                >
                  {t('aggiorna_stato')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Chat - Operator can chat with requester */}
        {isOperator && (
          <div className="mt-4 border border-[#B8D4E8]" data-testid="operator-chat-section">
            <ChatPanel requestId={request.id} />
          </div>
        )}

        <button
          onClick={onClose}
          className="mt-4 w-full py-2 border border-[#B8D4E8] text-[#4A6B8A] text-xs uppercase tracking-widest hover:border-[#FF3B30] hover:text-[#0D1B2A] transition-all"
          data-testid="close-modal-btn"
        >
          {t('annulla')}
        </button>
      </div>
    </div>
  );
}
